/**
 * What a person has said about being contacted, and why the platform believes it.
 *
 * Three outcomes, not two. `allow` permits engagement, `deny` is the person's
 * refusal, and `unknown` is everything else — no record, expired, purpose not
 * registered, subject not known. Silence is still refusal to act: `unknown`
 * never permits communication, and the screen says which of the three it is
 * rather than reducing it to a tick.
 *
 * Purposes are the registered vocabulary. Curating them is a data-protection
 * decision granted apart from reading a person's answer, so administering them
 * appears only for whoever holds it.
 *
 * Recording what a person said — a grant, a deny, or withdrawing an earlier
 * grant — is a steward's act (`NXP-P8-STEWARD-UI-001`), offered only on
 * `consent.record`. The platform guards withdrawal with the same authority as
 * recording, because it writes a person's answer too; so does this screen.
 * Nothing is edited or deleted: every answer is a new record beside the last.
 */

import { useCallback, useState } from "react";
import { api } from "../api/client";
import type {
  ConsentChannel,
  ConsentPurpose,
  ConsentRecord,
  ConsentResolution,
  Page,
} from "../api/types";
import { usePagedCollection } from "../api/useCollection";
import { useResource } from "../api/useResource";
import { change, reasonedRefusal, RequiredAuthority, Result, type Outcome } from "../components/governed";
import {
  Banner,
  Card,
  Classification,
  Fact,
  Facts,
  Identifier,
  Mono,
  More,
  Pill,
  when,
  type Tone,
} from "../components/primitives";
import { Empty, Show } from "../components/states";
import { useSession } from "../session/SessionProvider";

const OUTCOME_TONE: Record<ConsentResolution["outcome"], Tone> = {
  allow: "ok",
  deny: "bad",
  unknown: "wait",
};

/**
 * The three have to look different, not merely read differently.
 *
 * `unknown` is the one a UI is tempted to collapse — into an unticked box, or
 * into an error. It is neither. It does not permit engagement, and it is not the
 * person having refused; conflating it with `deny` would attribute a decision to
 * somebody who never made one.
 */
const OUTCOME_STATEMENT: Record<ConsentResolution["outcome"], { mark: string; says: string }> = {
  allow: { mark: "✓", says: "Engagement permitted" },
  deny: { mark: "⊘", says: "Explicit refusal or withdrawal — engagement not permitted" },
  unknown: {
    mark: "?",
    says: "No current affirmative evidence — engagement not permitted",
  },
};

const REASON_MEANING: Record<ConsentResolution["reason"], string> = {
  explicit_grant: "The person agreed, for this purpose and channel.",
  explicit_deny: "The person refused. That is their answer, not an absence of one.",
  withdrawn: "The person withdrew a previous agreement.",
  no_consent_on_record: "Nobody has recorded an answer. Silence is not agreement.",
  expired: "The agreement had an end, and it has passed.",
  subject_unknown: "No such subject in this tenant.",
  purpose_not_registered: "That purpose is not in the registered vocabulary.",
};

const CHANNELS: ConsentChannel[] = ["email", "phone", "sms", "post", "in_app", "any"];

/**
 * The page sizes this screen has always asked for.
 *
 * Unchanged on purpose. What changes is not how much is read at once but
 * whether the twenty-sixth recorded decision — or the hundred-and-first
 * registered purpose — is reachable at all: both used to be absent without the
 * screen ever saying so.
 */
const HISTORY_PAGE = 25;
const PURPOSE_PAGE = 100;

function Check() {
  const session = useSession();
  // The history is read by `GET /core/consent/{subject}/history`, which the
  // platform guards with `core.consent:read` — not `resolve`, which is what
  // offered this panel. Asked for on its own key, so a caller who may resolve
  // but not read is told so rather than shown a refusal they could not avoid.
  const mayReadHistory = session.may("consent.read");
  const [form, setForm] = useState({
    subject_entity_id: "",
    purpose: "",
    channel: "email" as ConsentChannel,
    country: "NL",
  });
  const [asked, setAsked] = useState<typeof form | null>(null);

  const read = useCallback(
    (signal: AbortSignal) =>
      api.get<ConsentResolution>("/core/consent/check", {
        params: asked ?? {},
        signal,
      }),
    [asked],
  );
  const { state, reload } = useResource<ConsentResolution>(read, { enabled: asked !== null });

  /**
   * A subject's history, read a page at a time.
   *
   * `resetKey` is the subject, because the subject is what the accumulated
   * pages belong to. Asking about a different person is a different history,
   * not more of the last one, and without this the second person's first page
   * would be appended under the first person's — one person's recorded
   * decisions shown as another's, which for consent evidence is the worst
   * possible way to be wrong.
   *
   * The read itself still re-issues on every question asked, exactly as it did
   * before this story, so pressing Check again re-reads rather than showing a
   * stale answer.
   *
   * The primitive reads pages as `PageOf`, which is narrower than the `Page` the
   * API returns: it does not carry `projection`. Nothing is discarded by that
   * here. `/core/consent/{subject}/history` answers from
   * `ConsentPolicyService.history`, which returns `Page.of(...)`; `Page.of`
   * never sets `projection` and the field defaults to `None`; and the only code
   * in the platform that ever constructs a `PageProjection` is the outbox
   * service, which does not serve this route. So `projection` is null on every
   * page this screen reads.
   */
  const historyPage = useCallback(
    (cursor: string | null, signal: AbortSignal) =>
      api.get<Page<ConsentRecord>>(`/core/consent/${asked?.subject_entity_id ?? ""}/history`, {
        params: { limit: HISTORY_PAGE, cursor },
        signal,
      }),
    [asked],
  );
  const history = usePagedCollection<ConsentRecord>(historyPage, {
    enabled: asked !== null && mayReadHistory,
    resetKey: asked?.subject_entity_id ?? "",
  });

  return (
    <div className="nx-split">
      <Card title="Ask about a person">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            setAsked({ ...form });
          }}
        >
          <div className="nx-field">
            <label htmlFor="consent-subject">Subject entity</label>
            <input
              id="consent-subject"
              className="nx-input"
              required
              value={form.subject_entity_id}
              onChange={(event) => {
                setForm({ ...form, subject_entity_id: event.target.value });
              }}
            />
            <span className="nx-hint">
              The canonical entity identifier. Email is never the durable identity key.
            </span>
          </div>
          <div className="nx-field">
            <label htmlFor="consent-purpose">Purpose</label>
            <input
              id="consent-purpose"
              className="nx-input"
              required
              value={form.purpose}
              onChange={(event) => {
                setForm({ ...form, purpose: event.target.value });
              }}
            />
          </div>
          <div className="nx-field">
            <label htmlFor="consent-channel">Channel</label>
            <select
              id="consent-channel"
              className="nx-select"
              value={form.channel}
              onChange={(event) => {
                setForm({ ...form, channel: event.target.value as ConsentChannel });
              }}
            >
              {CHANNELS.map((channel) => (
                <option key={channel} value={channel}>
                  {channel}
                </option>
              ))}
            </select>
          </div>
          <div className="nx-field">
            <label htmlFor="consent-country">Country</label>
            <input
              id="consent-country"
              className="nx-input"
              required
              maxLength={2}
              value={form.country}
              onChange={(event) => {
                setForm({ ...form, country: event.target.value.toUpperCase() });
              }}
            />
          </div>
          <button type="submit" className="nx-button nx-button--primary">
            Check
          </button>
        </form>
      </Card>

      {asked === null ? (
        <Card title="The answer">
          <p className="nx-hint">
            A resolution is for a purpose, a channel and a country at a moment — the same answer the
            platform gives a campaign, from the same service.
          </p>
        </Card>
      ) : (
        <div style={{ display: "grid", gap: 16 }}>
          <Card title="The answer">
            <Show resource={state} what="this consent resolution" onRetry={reload}>
              {(answer) => (
                <div style={{ display: "grid", gap: 12 }}>
                  <div role="group" aria-label="Consent outcome">
                    <Pill tone={OUTCOME_TONE[answer.outcome]}>{answer.outcome}</Pill>
                    <p style={{ margin: "8px 0 0", fontWeight: 600 }}>
                      <span aria-hidden="true" style={{ marginRight: 8 }}>
                        {OUTCOME_STATEMENT[answer.outcome].mark}
                      </span>
                      {OUTCOME_STATEMENT[answer.outcome].says}
                    </p>
                  </div>
                  <p style={{ margin: 0, color: "var(--nx-text-soft)" }}>
                    {REASON_MEANING[answer.reason]}
                  </p>
                  <Facts>
                    <Fact label="Purpose">{answer.scope.purpose}</Fact>
                    <Fact label="Channel">{answer.scope.channel}</Fact>
                    <Fact label="Country">{answer.scope.country}</Fact>
                    <Fact label="Asked about">{when(answer.at)}</Fact>
                    <Fact label="Decided">{when(answer.decided_at)}</Fact>
                    {answer.source ? (
                      <Fact label="Evidence">
                        {answer.source.source_system}
                        {answer.source.evidence_ref ? (
                          <span className="nx-hint"> · {answer.source.evidence_ref}</span>
                        ) : null}
                      </Fact>
                    ) : null}
                  </Facts>
                  <p className="nx-hint">
                    <Classification value="pii" /> A person&apos;s answer is personal data, and
                    reaching it is authorized per operation.
                  </p>
                </div>
              )}
            </Show>
          </Card>

          <Card title="What was recorded, and when">
            {mayReadHistory ? (
              <>
                <Show
                  resource={history.state}
                  what="this consent history"
                  onRetry={history.refresh}
                  empty={() =>
                    history.items.length === 0 ? (
                      <p className="nx-hint">Nothing on record for this subject.</p>
                    ) : null
                  }
                >
                  {() => (
                    <ul className="nx-timeline">
                      {history.items.map((record) => (
                        <li key={record.consent_id}>
                          <span
                            className={`nx-timeline__marker nx-pill--${
                              record.decision === "grant" ? "ok" : record.decision === "deny" ? "bad" : "quiet"
                            }`}
                          >
                            <span className="nx-dot" aria-hidden="true" />
                          </span>
                          <span>
                            <strong>{record.decision}</strong> for {record.scope.purpose} on{" "}
                            {record.scope.channel}
                            <span className="nx-hint" style={{ display: "block" }}>
                              {when(record.effective_from)}
                              {record.effective_to ? ` until ${when(record.effective_to)}` : " · still in force"}
                              {" · "}
                              <Identifier value={record.consent_id} />
                            </span>
                            {record.note ? <span className="nx-hint">{record.note}</span> : null}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </Show>
                <More
                  collection={history}
                  label="Show earlier decisions"
                  hint="This is one page of what was recorded for this person, newest first — not everything on record for them."
                />
              </>
            ) : (
              <RequiredAuthority
                capability="consent.read"
                what="Reading what was recorded for this person"
              />
            )}
          </Card>
        </div>
      )}
    </div>
  );
}

/**
 * Where the evidence of an answer came from. `nexus` is omitted: a person's
 * answer is never something Nexus itself originates.
 */
const SOURCE_KINDS: { value: string; label: string }[] = [
  { value: "manual", label: "Manual — captured by a person (a signed form, a call)" },
  { value: "connected_system", label: "A governed connected system" },
  { value: "import", label: "An import" },
  { value: "migration", label: "A migration" },
];

/** Now, as a `datetime-local` value in the browser's own zone. */
function nowLocal(): string {
  const now = new Date();
  now.setSeconds(0, 0);
  return new Date(now.getTime() - now.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
}

function RecordAnswer() {
  const [form, setForm] = useState({
    subject_entity_id: "",
    purpose: "",
    channel: "email" as ConsentChannel,
    country: "NL",
    decision: "grant" as "grant" | "deny",
    source_kind: "manual",
    source_system: "",
    source_record_id: "",
    evidence_ref: "",
    captured_at: nowLocal(),
    note: "",
  });
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  /** The fields both routes share, exactly as the API declares them. */
  const common = () => ({
    subject_entity_id: form.subject_entity_id.trim(),
    scope: { purpose: form.purpose.trim(), channel: form.channel, country: form.country },
    source: {
      source_system: form.source_system.trim(),
      source_kind: form.source_kind,
      captured_at: new Date(form.captured_at).toISOString(),
      ...(form.source_record_id.trim() === ""
        ? {}
        : { source_record_id: form.source_record_id.trim() }),
      ...(form.evidence_ref.trim() === "" ? {} : { evidence_ref: form.evidence_ref.trim() }),
    },
    ...(form.note.trim() === "" ? {} : { note: form.note.trim() }),
  });

  const scopeWords = `${form.purpose} on ${form.channel} in ${form.country}`;

  const send = (withdrawing: boolean) => {
    setBusy(true);
    void change(
      async (correlationId) => {
        // Neither route implements `Idempotency-Key`. Each answer is its own
        // record, so nothing here pretends a retry is safe.
        if (withdrawing) {
          await api.post<ConsentRecord>("/core/consent/withdraw", common(), { correlationId });
        } else {
          await api.post<ConsentRecord>(
            "/core/consent",
            { ...common(), decision: form.decision },
            { correlationId },
          );
        }
      },
      withdrawing ? "Withdrawing consent" : "Recording this answer",
      withdrawing
        ? `Withdrawal recorded for ${scopeWords}. It closes any grant for this scope from now on.`
        : `Recorded: ${form.decision} for ${scopeWords}.`,
      reasonedRefusal,
    )
      .then(setOutcome)
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <Card title="Record a person's answer">
      <p className="nx-hint" style={{ marginTop: 0 }}>
        <strong>Deny and withdrawal always beat a grant.</strong> Where a refusal or a withdrawal is
        in force at the same moment as a grant — including a blanket one, for any channel or any
        country — the answer is that engagement is not permitted. Nothing is overwritten: each
        answer is a new record, and what the person said before stays on record.
      </p>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          send(false);
        }}
      >
        <div className="nx-field">
          <label htmlFor="record-subject">Person (canonical entity)</label>
          <input
            id="record-subject"
            className="nx-input"
            required
            value={form.subject_entity_id}
            onChange={(event) => {
              setForm({ ...form, subject_entity_id: event.target.value });
            }}
          />
          <span className="nx-hint">The canonical entity identifier of the person.</span>
        </div>
        <div className="nx-field">
          <label htmlFor="record-purpose">Purpose code</label>
          <input
            id="record-purpose"
            className="nx-input"
            required
            maxLength={64}
            pattern="[a-z][a-z0-9_]*"
            value={form.purpose}
            onChange={(event) => {
              setForm({ ...form, purpose: event.target.value });
            }}
          />
          <span className="nx-hint">A registered purpose.</span>
        </div>
        <div className="nx-field">
          <label htmlFor="record-channel">Contact channel</label>
          <select
            id="record-channel"
            className="nx-select"
            value={form.channel}
            onChange={(event) => {
              setForm({ ...form, channel: event.target.value as ConsentChannel });
            }}
          >
            {CHANNELS.map((channel) => (
              <option key={channel} value={channel}>
                {channel}
              </option>
            ))}
          </select>
        </div>
        <div className="nx-field">
          <label htmlFor="record-country">Jurisdiction</label>
          <input
            id="record-country"
            className="nx-input"
            required
            minLength={2}
            maxLength={3}
            pattern="[A-Z]{2}|ANY"
            value={form.country}
            onChange={(event) => {
              setForm({ ...form, country: event.target.value.toUpperCase() });
            }}
          />
          <span className="nx-hint">ISO 3166-1 alpha-2, or ANY for every jurisdiction.</span>
        </div>
        <fieldset className="nx-field" style={{ border: 0, padding: 0, margin: "0 0 12px" }}>
          <legend>Their answer</legend>
          <label style={{ marginRight: 16 }}>
            <input
              type="radio"
              name="record-decision"
              value="grant"
              checked={form.decision === "grant"}
              onChange={() => {
                setForm({ ...form, decision: "grant" });
              }}
            />{" "}
            Grant
          </label>
          <label>
            <input
              type="radio"
              name="record-decision"
              value="deny"
              checked={form.decision === "deny"}
              onChange={() => {
                setForm({ ...form, decision: "deny" });
              }}
            />{" "}
            Deny
          </label>
        </fieldset>
        <div className="nx-field">
          <label htmlFor="record-source-kind">Where the answer came from</label>
          <select
            id="record-source-kind"
            className="nx-select"
            value={form.source_kind}
            onChange={(event) => {
              setForm({ ...form, source_kind: event.target.value });
            }}
          >
            {SOURCE_KINDS.map((kind) => (
              <option key={kind.value} value={kind.value}>
                {kind.label}
              </option>
            ))}
          </select>
        </div>
        <div className="nx-field">
          <label htmlFor="record-source-system">Source system</label>
          <input
            id="record-source-system"
            className="nx-input nx-mono"
            required
            maxLength={64}
            pattern="[a-z][a-z0-9_]*"
            value={form.source_system}
            onChange={(event) => {
              setForm({ ...form, source_system: event.target.value });
            }}
          />
          <span className="nx-hint">
            For a connected system, its registered key — Nexus refuses one its catalogue does not
            hold. Otherwise, where the evidence is kept.
          </span>
        </div>
        <div className="nx-field">
          <label htmlFor="record-source-record">Source record id (optional)</label>
          <input
            id="record-source-record"
            className="nx-input"
            maxLength={255}
            value={form.source_record_id}
            onChange={(event) => {
              setForm({ ...form, source_record_id: event.target.value });
            }}
          />
        </div>
        <div className="nx-field">
          <label htmlFor="record-evidence">Evidence reference (optional)</label>
          <input
            id="record-evidence"
            className="nx-input"
            maxLength={512}
            value={form.evidence_ref}
            onChange={(event) => {
              setForm({ ...form, evidence_ref: event.target.value });
            }}
          />
        </div>
        <div className="nx-field">
          <label htmlFor="record-captured">Captured at</label>
          <input
            id="record-captured"
            type="datetime-local"
            className="nx-input"
            required
            value={form.captured_at}
            onChange={(event) => {
              setForm({ ...form, captured_at: event.target.value });
            }}
          />
          <span className="nx-hint">When the person gave this answer — not when it is entered.</span>
        </div>
        <div className="nx-field">
          <label htmlFor="record-note">Note (optional)</label>
          <input
            id="record-note"
            className="nx-input"
            maxLength={512}
            value={form.note}
            onChange={(event) => {
              setForm({ ...form, note: event.target.value });
            }}
          />
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button type="submit" className="nx-button nx-button--primary" disabled={busy}>
            Record answer
          </button>
          <button
            type="button"
            className="nx-button"
            disabled={busy}
            onClick={(event) => {
              // The same required fields as recording, checked the same way.
              const owner = event.currentTarget.form;
              if (owner !== null && !owner.reportValidity()) return;
              send(true);
            }}
          >
            Withdraw consent
          </button>
        </div>
        <p className="nx-hint" style={{ marginTop: 8 }}>
          Withdrawing takes back a permission for this exact scope. It does not delete the grant: that
          the person agreed, and that they later changed their mind, are both facts.
        </p>
      </form>
      <Result outcome={outcome} />
    </Card>
  );
}

/**
 * Adding a purpose to the vocabulary.
 *
 * Neither purpose route implements `Idempotency-Key`, so none is sent. A retry
 * of a registration that did land is refused by the platform as a duplicate
 * code, which is the honest answer rather than a replay.
 */
function RegisterPurpose({ onOutcome }: { onOutcome: (outcome: Outcome) => void }) {
  const [form, setForm] = useState({ code: "", display_name: "", owner: "" });
  const [busy, setBusy] = useState(false);
  return (
    <form
      aria-labelledby="register-purpose-title"
      style={{ padding: "12px 18px 18px" }}
      onSubmit={(event) => {
        event.preventDefault();
        setBusy(true);
        const code = form.code.trim();
        void change(
          (correlationId) =>
            api.post<ConsentPurpose>(
              "/core/consent/purposes",
              {
                code,
                display_name: form.display_name.trim(),
                owner: form.owner.trim(),
              },
              { correlationId },
            ),
          "Registering a purpose",
          `Registered ${code}. People can now be asked about it; nobody has answered yet, so every question about it still answers no until they do.`,
          reasonedRefusal,
        )
          .then((result) => {
            onOutcome(result);
            if (result.tone === "ok") setForm({ code: "", display_name: "", owner: "" });
          })
          .finally(() => {
            setBusy(false);
          });
      }}
    >
      <h3 id="register-purpose-title" style={{ fontSize: "var(--nx-text-base)", margin: "0 0 10px" }}>
        Register a purpose
      </h3>
      <div className="nx-field">
        <label htmlFor="purpose-code">Code</label>
        <input
          id="purpose-code"
          className="nx-input nx-mono"
          required
          maxLength={64}
          pattern="[a-z][a-z0-9_]*"
          aria-describedby="purpose-code-hint"
          value={form.code}
          onChange={(event) => {
            setForm({ ...form, code: event.target.value });
          }}
        />
        <span className="nx-hint" id="purpose-code-hint">
          What applications name when they ask: lowercase letters, digits and &apos;_&apos;,
          starting with a letter.
        </span>
      </div>
      <div className="nx-field">
        <label htmlFor="purpose-name">Display name</label>
        <input
          id="purpose-name"
          className="nx-input"
          required
          maxLength={128}
          value={form.display_name}
          onChange={(event) => {
            setForm({ ...form, display_name: event.target.value });
          }}
        />
      </div>
      <div className="nx-field">
        <label htmlFor="purpose-owner">Accountable owner</label>
        <input
          id="purpose-owner"
          className="nx-input"
          required
          maxLength={256}
          value={form.owner}
          onChange={(event) => {
            setForm({ ...form, owner: event.target.value });
          }}
        />
      </div>
      <button type="submit" className="nx-button nx-button--primary" disabled={busy}>
        Register purpose
      </button>
    </form>
  );
}

function DeactivatePurpose({
  purpose,
  onOutcome,
}: {
  purpose: ConsentPurpose;
  onOutcome: (outcome: Outcome) => void;
}) {
  const [busy, setBusy] = useState(false);
  return (
    <button
      type="button"
      className="nx-button"
      disabled={busy}
      aria-label={`Deactivate ${purpose.code}`}
      onClick={() => {
        setBusy(true);
        void change(
          (correlationId) =>
            api.post<ConsentPurpose>(
              `/core/consent/purposes/${encodeURIComponent(purpose.code)}/deactivate`,
              {},
              { correlationId },
            ),
          "Deactivating a purpose",
          `Deactivated ${purpose.code}. Nobody is asked about it any more, and every question about it answers no. What people already said about it stays on record, unchanged.`,
          reasonedRefusal,
        )
          .then(onOutcome)
          .finally(() => {
            setBusy(false);
          });
      }}
    >
      Deactivate
    </button>
  );
}

function Purposes() {
  const session = useSession();
  const mayAdminister = session.may("consent_purpose.administer");
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  /**
   * `projection` is null here for the same reason it is null on the history:
   * `/core/consent/purposes` answers from `ConsentPolicyService.purposes`,
   * which returns `Page.of(...)`, and `Page.of` never sets it.
   */
  const readPage = useCallback(
    (cursor: string | null, signal: AbortSignal) =>
      api.get<Page<ConsentPurpose>>("/core/consent/purposes", {
        params: { limit: PURPOSE_PAGE, cursor },
        signal,
      }),
    [],
  );
  const purposes = usePagedCollection<ConsentPurpose>(readPage);

  const changed = (result: Outcome) => {
    setOutcome(result);
    if (result.tone === "ok") purposes.refresh();
  };

  return (
    <Card
      title="Registered purposes"
      actions={
        <button type="button" className="nx-button" onClick={purposes.refresh}>
          Refresh
        </button>
      }
      bodyless
    >
      <Show
        resource={purposes.state}
        what="the consent purposes"
        onRetry={purposes.refresh}
        empty={() =>
          purposes.items.length === 0 ? (
            <Empty
              title="No purposes registered."
              hint="A purpose has to be registered before anybody can be asked about it — every question about an unregistered one answers purpose_not_registered, which is no."
            />
          ) : null
        }
      >
        {() => (
          <div className="nx-table-wrap">
            <table className="nx-table">
              <caption className="nx-visually-hidden">The registered consent vocabulary</caption>
              <thead>
                <tr>
                  <th scope="col">Purpose</th>
                  <th scope="col">Owner</th>
                  <th scope="col">State</th>
                  <th scope="col">Registered</th>
                  {mayAdminister ? (
                    <th scope="col">
                      <span className="nx-visually-hidden">Change</span>
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {purposes.items.map((purpose) => (
                  <tr key={purpose.purpose_id}>
                    <td>
                      <span className="nx-table__primary">{purpose.display_name}</span>
                      <span className="nx-table__secondary" style={{ display: "block" }}>
                        {purpose.code}
                      </span>
                    </td>
                    <td>{purpose.owner}</td>
                    <td>
                      <Pill tone={purpose.active ? "ok" : "quiet"}>
                        {purpose.active ? "active" : "deactivated"}
                      </Pill>
                    </td>
                    <td>{when(purpose.registered_at)}</td>
                    {mayAdminister ? (
                      <td>
                        {/* A deactivated purpose has nothing left to deactivate. */}
                        {purpose.active ? (
                          <DeactivatePurpose purpose={purpose} onOutcome={changed} />
                        ) : null}
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Show>
      <More
        collection={purposes}
        label="Show more purposes"
        hint="This is one page of the registered vocabulary, not all of it. A purpose that is registered but not shown here still resolves."
        placement="bodyless"
      />
      <p className="nx-hint" style={{ padding: "12px 18px 0" }}>
        Curating this vocabulary is a data-protection decision, granted apart from reading it. A
        purpose that is not registered makes every consent question about it answer{" "}
        <Mono>purpose_not_registered</Mono> — which is no.
      </p>
      {mayAdminister ? (
        <RegisterPurpose onOutcome={changed} />
      ) : (
        <p className="nx-hint" style={{ padding: "8px 18px 12px" }}>
          Registering and deactivating a purpose is not permitted for you.
        </p>
      )}
      <div style={{ padding: "0 18px" }}>
        <Result outcome={outcome} />
      </div>
    </Card>
  );
}

export function Consent() {
  const session = useSession();
  return (
    <>
      <Banner title="Unknown never permits communication.">
        Three outcomes, not two: <strong>allow</strong>, the person&apos;s <strong>deny</strong>, and{" "}
        <strong>unknown</strong> for everything else. Silence is still refusal to act.
      </Banner>
      <div style={{ display: "grid", gap: 18 }}>
        <Purposes />
        {session.may("consent.record") ? (
          <RecordAnswer />
        ) : (
          <Card title="Record a person's answer">
            <RequiredAuthority capability="consent.record" what="Recording or withdrawing consent" />
            <p className="nx-hint">
              A person&apos;s answer is personal data, so this needs a role that grants it and an
              access policy a data owner activates — either could be what is missing here.
            </p>
          </Card>
        )}
        {session.may("consent.resolve") ? (
          <Check />
        ) : (
          <Card title="Ask about a person">
            <p className="nx-hint">
              Reaching a person&apos;s answer is not permitted for you. It is granted apart from
              reading the vocabulary, and because the answer is personal data it also needs a policy
              a data owner activates — either could be what is missing here.
            </p>
          </Card>
        )}
      </div>
    </>
  );
}
